import {
  useGetPrePumpTradesQuery,
  useGetPrePumpSummaryQuery,
} from "@/store/api";
import { PrePumpCard } from "@/components/domain/PrePumpCard";
import { PrePumpBacktestCard } from "@/components/domain/PrePumpBacktestCard";
import { PrePumpWatchlistCard } from "@/components/domain/PrePumpWatchlistCard";
import { ConvictionDcaCard } from "@/components/domain/ConvictionDcaCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StalenessBadge } from "@/components/domain/StalenessBadge";
import { EngineStateBanner } from "@/components/domain/EngineStateBanner";
import { PrePumpLearnedPatternCard } from "@/components/domain/PrePumpLearnedPatternCard";
import { Badge } from "@/components/ui/badge";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { PnLPill } from "@/components/domain/PnLPill";
import { fmtUsd, fmtTime } from "@/lib/format";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

/**
 * Pre-Pump Trades page — admin-only.
 *
 * One-stop view of every trade initiated from a pre-pump signal:
 *  manual_override   (operator buys like the MEME today)
 *  library_engine    (exchange_outflow_accumulation, momentum_cont, etc.)
 *  opportunity_engine (early_momentum / breakout_continuation)
 *  breakout_engine   (BB squeeze release)
 *
 * Sections:
 *  1. PrePumpCard — today's top candidates (already on Overview, repeated here)
 *  2. Summary KPIs — total trades, win rate, realized P&L
 *  3. Equity curve (cumulative realized P&L line chart)
 *  4. Open positions with live unrealized P&L
 *  5. Closed positions history
 */
export default function PrePumpTrades() {
  const opens = useGetPrePumpTradesQuery("open", { pollingInterval: 30000 });
  const closed = useGetPrePumpTradesQuery("closed", { pollingInterval: 60000 });
  const summary = useGetPrePumpSummaryQuery(undefined, { pollingInterval: 60000 });

  const t = summary.data?.totals;
  const byEngine = summary.data?.by_engine ?? {};
  const curve = summary.data?.equity_curve ?? [];

  // Aggregate live unrealized
  const totalUnreal = (opens.data || [])
    .reduce((a: number, r: any) => a + (r.unrealized_usd ?? 0), 0);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Pre-pump trades</h1>
        <p className="text-sm text-muted">
          Trades sourced from pre-pump signals: manual operator buys,
          library-engine pattern matches, opportunity-engine ADK picks,
          and breakout-engine squeeze releases.
        </p>
      </header>

      {/* Engine state — each contributing engine, paused state surfaced */}
      <EngineStateBanner
        enabledKey="pre_pump_auto_trader_enabled"
        label="Pre-pump auto-trader"
        hostHint="Manual buys from the candidates table still work."
      />
      <EngineStateBanner
        enabledKey="opportunity_engine_enabled"
        label="Opportunity engine"
        hostHint="Governor auto-arms this in favorable BTC regime."
      />
      <EngineStateBanner
        enabledKey="breakout_engine_enabled"
        label="Breakout engine"
        hostHint="Governor auto-arms this in favorable BTC regime."
      />

      {/* Learned pre-pump pattern — Gemini studies recent winners daily */}
      <PrePumpLearnedPatternCard />

      {/* Section 1: live candidates */}
      <PrePumpCard />

      {/* Watchlist — symbols being stalked before commit */}
      <PrePumpWatchlistCard />

      {/* Conviction DCA — deep-research-driven aggressive accumulation */}
      <ConvictionDcaCard />

      {/* Backtest — historical validation of the entry/exit rules */}
      <PrePumpBacktestCard />

      {/* Section 2: summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard
          label="Total trades"
          value={t ? String(t.trades) : "—"}
          sub={t ? `${t.wins} wins (${t.win_rate.toFixed(0)}%)` : ""}
        />
        <KpiCard
          label="Realized P&L"
          value={t ? `${t.realized_pnl >= 0 ? "+" : ""}${fmtUsd(t.realized_pnl)}` : "—"}
          tone={t && t.realized_pnl >= 0 ? "good" : "bad"}
        />
        <KpiCard
          label="Open positions"
          value={String(opens.data?.length ?? 0)}
          sub={opens.data && opens.data.length > 0
            ? `unrealized ${totalUnreal >= 0 ? "+" : ""}${fmtUsd(totalUnreal)}`
            : "—"}
        />
        <KpiCard
          label="Win rate"
          value={t ? `${t.win_rate.toFixed(0)}%` : "—"}
          sub="across all engines"
        />
        <KpiCard
          label="Best engine"
          value={
            Object.keys(byEngine).length === 0
              ? "—"
              : Object.entries(byEngine)
                  .sort((a: any, b: any) => b[1].net_pnl - a[1].net_pnl)[0][0]
                  .replace("_engine", "").replace("_override", "")
          }
        />
      </div>

      {/* Section 3: equity curve */}
      <Card>
        <CardHeader>
          <CardTitle>Cumulative realized P&L</CardTitle>
        </CardHeader>
        <CardContent style={{ height: 240 }}>
          {summary.isLoading ? (
            <Skeleton className="h-full w-full" />
          ) : curve.length < 2 ? (
            <div className="text-sm text-muted">
              (need at least 2 closed trades for the curve)
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={curve}>
                <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                <XAxis
                  dataKey="ts"
                  tickFormatter={(s) => s ? s.substring(5, 10) : ""}
                  tick={{ fontSize: 10 }}
                />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip
                  contentStyle={{ background: "#111", border: "1px solid #333" }}
                  labelFormatter={(s) => `${s}`}
                  formatter={(v: any) => [`${fmtUsd(v)}`, "cum P&L"]}
                />
                <Line
                  type="monotone"
                  dataKey="cum_pnl"
                  stroke="#22c55e"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Section 4: open positions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2">
            <span>Open positions ({opens.data?.length ?? 0})</span>
            <StalenessBadge
              ts={(opens.data ?? []).reduce<string | null>(
                (m, r: any) => {
                  const t = r.opened_at;
                  return t && (!m || t > m) ? t : m;
                }, null)}
              warnHours={4} staleHours={24} veryStaleHours={72}
              label="newest fire"
            />
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!opens.data || opens.data.length === 0 ? (
            <div className="text-sm text-muted">(none open)</div>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Symbol</TH>
                  <TH>Engine</TH>
                  <TH className="text-right">Entry</TH>
                  <TH className="text-right">Now</TH>
                  <TH className="text-right">Δ%</TH>
                  <TH className="text-right">Unrealized</TH>
                  <TH className="text-right">Stop</TH>
                  <TH className="text-right">Target</TH>
                  <TH>Trail</TH>
                </TR>
              </THead>
              <TBody>
                {opens.data.map((r: any) => (
                  <TR key={r.id}>
                    <TD className="font-mono font-semibold">{r.symbol}</TD>
                    <TD className="text-xs">
                      <Badge tone="info">
                        {(r.planner_model || "?").replace("_engine", "").replace("_override", "")}
                      </Badge>
                    </TD>
                    <TD className="text-right tabular-nums">
                      {r.entry_price?.toFixed(r.entry_price >= 100 ? 2 : 6)}
                    </TD>
                    <TD className="text-right tabular-nums">
                      {r.current_price?.toFixed(r.current_price >= 100 ? 2 : 6) ?? "—"}
                    </TD>
                    <TD className={`text-right tabular-nums ${
                      (r.unrealized_pct ?? 0) >= 0 ? "text-green-500" : "text-red-500"
                    }`}>
                      {r.unrealized_pct == null
                        ? "—"
                        : `${r.unrealized_pct >= 0 ? "+" : ""}${r.unrealized_pct.toFixed(2)}%`}
                    </TD>
                    <TD className="text-right">
                      {r.unrealized_usd != null ? <PnLPill value={r.unrealized_usd} /> : "—"}
                    </TD>
                    <TD className="text-right tabular-nums text-xs">
                      {r.stop_price?.toFixed(r.stop_price >= 100 ? 2 : 6)}
                    </TD>
                    <TD className="text-right tabular-nums text-xs">
                      {r.target_price?.toFixed(r.target_price >= 100 ? 2 : 6)}
                    </TD>
                    <TD className="text-xs">
                      {r.trail_active ? (
                        <Badge tone="good">ARMED</Badge>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Section 5: closed history */}
      <Card>
        <CardHeader>
          <CardTitle>Closed history ({closed.data?.length ?? 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {closed.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : !closed.data || closed.data.length === 0 ? (
            <div className="text-sm text-muted">(no closed trades yet)</div>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Closed</TH>
                  <TH>Symbol</TH>
                  <TH>Engine</TH>
                  <TH>Status</TH>
                  <TH className="text-right">Entry</TH>
                  <TH className="text-right">Exit</TH>
                  <TH className="text-right">Net P&L</TH>
                </TR>
              </THead>
              <TBody>
                {closed.data.slice(0, 50).map((r: any) => {
                  const net = r.net_pnl_usd ?? 0;
                  return (
                    <TR key={r.id}>
                      <TD className="text-xs whitespace-nowrap">
                        {r.exit_time ? fmtTime(r.exit_time) : "—"}
                      </TD>
                      <TD className="font-mono">{r.symbol}</TD>
                      <TD className="text-xs">
                        {(r.planner_model || "?").replace("_engine", "").replace("_override", "")}
                      </TD>
                      <TD className="text-xs text-muted">{r.status}</TD>
                      <TD className="text-right tabular-nums text-xs">
                        {r.entry_price?.toFixed(r.entry_price >= 100 ? 2 : 6)}
                      </TD>
                      <TD className="text-right tabular-nums text-xs">
                        {r.exit_price?.toFixed(r.exit_price >= 100 ? 2 : 6)}
                      </TD>
                      <TD className={`text-right font-semibold tabular-nums ${
                        net > 0 ? "text-green-500" : net < 0 ? "text-red-500" : "text-muted"
                      }`}>
                        {net >= 0 ? "+" : ""}{fmtUsd(net)}
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({ label, value, sub, tone }: {
  label: string; value: string; sub?: string; tone?: "good" | "bad" | "neutral";
}) {
  const valColor = tone === "good" ? "text-green-500"
                 : tone === "bad" ? "text-red-500"
                                  : "";
  return (
    <Card>
      <CardContent className="pt-4">
        <div className="text-xs text-muted">{label}</div>
        <div className={`text-xl font-semibold tabular-nums ${valColor}`}>
          {value}
        </div>
        {sub && <div className="text-xs text-muted mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}
